import transform from 'lodash-es/transform';
import { schema } from 'normalizr';
import type { Beacon } from '../slices/beacons';
import type { Cluster } from '../slices/clusters';
import type { Device, DeviceModel, EquipmentHasDevice } from '../slices/devices';
import type { Equipment, EquipmentModelEntity } from '../slices/equipments';
import type { ClusterParcel, Parcel } from '../slices/parcels';
import type { EquipmentSession, ParcelSession } from '../slices/sessions';
import type { User } from '../slices/users';
import { KeysToCamelCase } from '../utils/types';

const { Entity } = schema;

const underscoreToCamelCase = (word: string) => {
  if (typeof word === 'string') {
    return word.replace(/_(.)/g, (match) => match[1]!.toUpperCase());
  }
  return word;
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && value.constructor === Object;
}

export function transformEntityWithCamelCase<T>(entity: T): KeysToCamelCase<T>;
export function transformEntityWithCamelCase(entity: unknown): unknown {
  return transform(
    entity as Record<string, unknown>,
    (result: Record<string, unknown>, value, key) => {
      if (key === 'authorizations') {
        result[key] = value;
        return;
      }
      const newKey = underscoreToCamelCase(key);
      if (isPlainObject(value)) {
        result[newKey] = transformEntityWithCamelCase(value);
      } else {
        result[newKey] = value;
      }
    },
    {},
  );
}
// User
const user = new Entity<User>('user');
const withAuthor = { author: user, lastUpdater: user };

// Rights
const rights = new Entity('rights');

// Clusters
const cluster = new Entity<Cluster>('cluster', { ...withAuthor, rights });

// Parcels
export const parcel = new Entity<Parcel>('parcel', {
  ...withAuthor,
  cluster,
});

// Devices
const deviceModel = new Entity(
  'deviceModel',
  { ...withAuthor },
  {
    idAttribute: 'equipmentType',
    processStrategy: (entity: DeviceModel) => transformEntityWithCamelCase(entity),
  },
);
export const deviceInstance = new Entity(
  'deviceInstance',
  {
    ...withAuthor,
    cluster,
    deviceModel,
  },
  {
    processStrategy: (entity: Device) =>
      transformEntityWithCamelCase({
        ...entity,
        // devices have (0,0) location when the geolocation is hidden, so we return undefined instead
        location_latitude: entity.location_latitude || undefined,
        location_longitude: entity.location_longitude || undefined,
      }),
  },
);

// Beacons
export const beacon = new Entity(
  'beacon',
  {},
  {
    idAttribute: 'uuid',
    processStrategy: (entity: Beacon) => transformEntityWithCamelCase(entity),
  },
);

// Equipments
export const equipmentModel = new Entity(
  'equipmentModel',
  { ...withAuthor },
  {
    processStrategy: (entity: EquipmentModelEntity) => ({
      ...transformEntityWithCamelCase(entity),
      equipmentType: entity.type,
    }),
  },
);
export const equipmentInstance = new Entity(
  'equipmentInstance',
  {
    ...withAuthor,
  },
  {
    processStrategy: (entity: Equipment) => transformEntityWithCamelCase(entity),
  },
);

export const equipmentHasDevice = new Entity(
  'equipmentHasDevice',
  {
    ...withAuthor,
    deviceInstance,
    equipmentInstance,
  },
  {
    processStrategy: (entity: EquipmentHasDevice) => transformEntityWithCamelCase(entity),
  },
);
equipmentInstance.define({ equipmentHasDevices: [equipmentHasDevice] });

// Sessions
export const parcelSession = new Entity(
  'parcelSession',
  {
    ...withAuthor,
    parcel,
  },
  {
    processStrategy: (entity: ParcelSession) => transformEntityWithCamelCase(entity),
  },
);

export const equipmentSession = new Entity(
  'equipmentSession',
  {
    parcelSessions: [parcelSession],
  },
  {
    processStrategy: (entity: EquipmentSession) => ({
      ...transformEntityWithCamelCase(entity),
    }),
  },
);

export const clusterParcel = new Entity(
  'clusterParcel',
  {
    parcel,
    cluster,
  },
  {
    processStrategy: (entity: ClusterParcel) => transformEntityWithCamelCase(entity),
  },
);
